/**
 * S2b — salience coder-reliability gold marking (responsive, mobile-first).
 *
 * Marks the blind eval set served by /api/salience/eval/items against the
 * agent-radar schema: relevance gate → (if relevant) frame / stance / tone /
 * entity-mentions / tagline. The model's own labels are NEVER fetched here —
 * blind discipline — so the human isn't anchored. scripts/score_salience_eval.py
 * joins these gold marks to the hidden model answers and reports Krippendorff α.
 *
 * One item at a time (a card flow that works on a phone): pick relevance, then
 * the dependent dimensions appear; Save advances to the next unmarked item.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  apiSalience,
  SALIENCE_FRAMES,
  SALIENCE_STANCES,
  SALIENCE_TONES,
  type SalienceEvalItem,
  type SalienceMark,
} from "../lib/api";

type Draft = {
  relevant: "" | "yes" | "no";
  frame: string;
  stance: string;
  tone: string;
  entities: string[];
  tagline: boolean;
  notes: string;
};

function draftFrom(item: SalienceEvalItem): Draft {
  return {
    relevant: item.gold_relevant,
    frame: item.gold_frame,
    stance: item.gold_stance,
    tone: item.gold_tone,
    entities: item.gold_entity_mentions ?? [],
    tagline: item.gold_tagline_matched,
    notes: item.gold_notes ?? "",
  };
}

/** Single-select segmented control — large tap targets for mobile. */
function SegGroup({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(active ? "" : opt)}
              className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                active
                  ? "border-brand-indigo bg-brand-indigo text-ink-on-dark"
                  : "border-rule bg-surface text-ink hover:border-brand-indigo"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Multi-select chips for entity mentions. */
function ChipMulti({
  label,
  selected,
  options,
  onToggle,
}: {
  label: string;
  selected: string[];
  options: { id: string; label: string }[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
        {label}{" "}
        <span className="font-normal normal-case text-ink-muted">
          (tap all that the item names)
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onToggle(opt.id)}
              className={`rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${
                active
                  ? "border-brand-indigo bg-brand-indigo text-ink-on-dark"
                  : "border-rule bg-surface text-ink hover:border-brand-indigo"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  hansard: "Hansard",
  edm: "Early Day Motion",
  wpq: "Written Question",
  petition: "Petition",
  govuk: "GOV.UK",
  youtube: "YouTube",
};

export function SalienceEvalMarker() {
  const itemsQuery = useQuery({
    queryKey: ["salience-eval-items"],
    queryFn: apiSalience.eval.items,
  });
  const entitiesQuery = useQuery({
    queryKey: ["salience-entities"],
    queryFn: apiSalience.entities,
  });

  // Local working copy so saves update progress without a refetch.
  const [items, setItems] = useState<SalienceEvalItem[]>([]);
  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (itemsQuery.data) {
      setItems(itemsQuery.data.items);
      // Start at the first unmarked item.
      const first = itemsQuery.data.items.findIndex((i) => !i.marked);
      setIdx(first === -1 ? 0 : first);
    }
  }, [itemsQuery.data]);

  const current = items[idx];
  useEffect(() => {
    if (current) setDraft(draftFrom(current));
    setError(null);
  }, [current]);

  const entityOptions = useMemo(
    () =>
      (entitiesQuery.data ?? []).map((t) => ({ id: t.term_id, label: t.label })),
    [entitiesQuery.data],
  );
  const markedCount = useMemo(
    () => items.filter((i) => i.marked).length,
    [items],
  );

  if (itemsQuery.isLoading)
    return <div className="py-12 text-center text-ink-muted">Loading eval set…</div>;
  if (itemsQuery.error)
    return (
      <div className="py-12 text-center text-sentiment-unsupportive">
        Failed to load eval set. Is the salience backend wired into the proxy?
      </div>
    );
  if (!items.length)
    return (
      <div className="py-12 text-center text-ink-muted">
        No eval items yet — run <code>scripts/build_salience_eval_set.py</code> to
        populate the gold set.
      </div>
    );
  if (!current || !draft) return null;

  const setField = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  const advance = () => {
    // Prefer the next unmarked item after this one; else just step forward.
    const after = items.findIndex((i, j) => j > idx && !i.marked);
    if (after !== -1) setIdx(after);
    else if (idx < items.length - 1) setIdx(idx + 1);
  };

  const save = async () => {
    if (draft.relevant !== "yes" && draft.relevant !== "no") {
      setError("Pick whether the item is relevant first.");
      return;
    }
    setSaving(true);
    setError(null);
    const mark: SalienceMark = {
      relevant: draft.relevant,
      frame: draft.frame,
      stance: draft.stance,
      tone: draft.tone,
      entity_mentions: draft.entities,
      tagline_matched: draft.tagline,
      notes: draft.notes,
    };
    try {
      const { item } = await apiSalience.eval.save(current.id, mark);
      setItems((prev) => prev.map((it) => (it.id === item.id ? item : it)));
      advance();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const relevant = draft.relevant === "yes";
  const pct = items.length ? Math.round((markedCount / items.length) * 100) : 0;

  return (
    <div className="mx-auto max-w-2xl pb-28">
      {/* Header + progress */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-ink">Salience eval — gold marking</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          Mark each item against the agent-radar schema. The classifier's answer
          is hidden. <strong className="text-ink">Judge only on the excerpt shown</strong>{" "}
          — it's the same text the classifier saw, so reliability is measured on
          equal evidence. If a snippet is too thin to tell, make your best call
          and jot “thin” in notes; don't open the source to decide.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-rule">
            <div
              className="h-full bg-brand-indigo transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="shrink-0 text-sm font-medium text-ink-muted">
            {markedCount}/{items.length} marked
          </span>
        </div>
      </div>

      {/* Item navigator */}
      <div className="mb-3 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => setIdx(Math.max(0, idx - 1))}
          disabled={idx === 0}
          className="rounded-md border border-rule px-3 py-1.5 font-medium text-ink disabled:opacity-40"
        >
          ← Prev
        </button>
        <span className="text-ink-muted">
          Item {idx + 1} of {items.length}
          {current.marked && (
            <span className="ml-2 rounded-full bg-sentiment-supportive/15 px-2 py-0.5 text-[12px] font-medium text-sentiment-supportive">
              marked
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => setIdx(Math.min(items.length - 1, idx + 1))}
          disabled={idx === items.length - 1}
          className="rounded-md border border-rule px-3 py-1.5 font-medium text-ink disabled:opacity-40"
        >
          Skip →
        </button>
      </div>

      {/* Content card (blind) */}
      <div className="mb-5 rounded-lg border border-rule bg-surface p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[12px]">
          <span className="rounded bg-brand-indigo/10 px-2 py-0.5 font-semibold text-brand-indigo">
            {SOURCE_LABEL[current.source] ?? current.source}
          </span>
          <span className="text-ink-muted">
            term: <span className="font-medium text-ink">{current.term_id}</span>
          </span>
          {current.matched_phrase && (
            <span className="text-ink-muted">
              matched: “{current.matched_phrase}”
            </span>
          )}
        </div>
        {current.title && (
          <div className="mb-1.5 font-serif text-lg font-semibold leading-snug text-ink">
            {current.title}
          </div>
        )}
        <div className="max-h-72 overflow-y-auto whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-ink">
          {current.text || (
            <span className="italic text-ink-muted">(no body text — judge on the title)</span>
          )}
        </div>
        {current.url && (
          <a
            href={current.url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-block text-[13px] font-medium text-brand-indigo underline"
          >
            open source ↗
          </a>
        )}
      </div>

      {/* Marking controls */}
      <SegGroup
        label="Relevant to the wealth-tax conversation?"
        value={draft.relevant}
        options={["yes", "no"]}
        onChange={(v) => setField("relevant", v as Draft["relevant"])}
      />

      {relevant && (
        <>
          <SegGroup
            label="Frame (dominant problem-definition)"
            value={draft.frame}
            options={SALIENCE_FRAMES}
            onChange={(v) => setField("frame", v)}
          />
          <SegGroup
            label="Stance (toward the wealth-tax position)"
            value={draft.stance}
            options={SALIENCE_STANCES}
            onChange={(v) => setField("stance", v)}
          />
          <SegGroup
            label="Tone (low-confidence)"
            value={draft.tone}
            options={SALIENCE_TONES}
            onChange={(v) => setField("tone", v)}
          />
          <ChipMulti
            label="Entity mentions"
            selected={draft.entities}
            options={entityOptions}
            onToggle={(id) =>
              setField(
                "entities",
                draft.entities.includes(id)
                  ? draft.entities.filter((e) => e !== id)
                  : [...draft.entities, id],
              )
            }
          />
          <label className="mb-4 flex items-center gap-2.5">
            <input
              type="checkbox"
              checked={draft.tagline}
              onChange={(e) => setField("tagline", e.target.checked)}
              className="h-5 w-5 rounded border-rule accent-brand-indigo"
            />
            <span className="text-sm font-medium text-ink">
              Reproduces the tagline (“tax wealth, not work” or a close paraphrase)
            </span>
          </label>
        </>
      )}

      <div className="mb-4">
        <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
          Notes (optional)
        </div>
        <textarea
          value={draft.notes}
          onChange={(e) => setField("notes", e.target.value)}
          rows={2}
          className="w-full rounded-md border border-rule bg-surface p-2 text-sm text-ink focus:border-brand-indigo focus:outline-none"
          placeholder="Anything ambiguous worth recording…"
        />
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-sentiment-unsupportive/30 bg-sentiment-unsupportive/10 px-3 py-2 text-sm text-sentiment-unsupportive">
          {error}
        </div>
      )}

      {/* Sticky save bar — thumb-reachable on mobile */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-rule bg-surface/95 px-4 py-3 backdrop-blur md:left-70">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex-1 rounded-md bg-brand-indigo px-4 py-2.5 text-center text-sm font-semibold text-ink-on-dark transition-colors hover:bg-brand-indigo-deep disabled:opacity-50"
          >
            {saving ? "Saving…" : current.marked ? "Update & next →" : "Save & next →"}
          </button>
        </div>
      </div>
    </div>
  );
}
